import {
  GS1_PRIMARY_KEY_OPTIONS,
  parseGS1DigitalLink,
  type PrimaryKeyAI,
  type PrimaryKeyOption,
} from '@myplayer/gs1';
import {
  IdentifierDraftSchema,
  LINK_TYPE_OPTIONS,
  TargetLinkSchema,
  isIdentifierRecalled,
  type ConditionalRoutingRule,
  type DraftTargetLink,
  type IdentifierDisplayStatus,
  type IdentifierDraft,
  type IdentifierRecord,
  type IdentifierStatus,
  type SectorId,
  type StagedDraftRecord,
} from '@myplayer/schema';
import { validateIdentifierCandidate } from '@myplayer/validation';
import { doc, serverTimestamp, setDoc, updateDoc, type Firestore } from 'firebase/firestore';
import { checkFirestoreOrgIdentifierUniqueness } from './products-uniqueness.js';

export {
  CROSS_ORG_GTIN_CONFLICT_MESSAGE,
  buildRecordConflictKey,
  buildSameOrgGtinConflictMessage,
  checkAdminIdentifierUniqueness,
  checkFirestoreOrgIdentifierUniqueness,
  computeDuplicateIdentifierConflicts,
  getDuplicateConflictForRecord,
  parseRecordCreatedAtMs,
  type DuplicateIdentifierConflict,
} from './products-uniqueness.js';

export {
  GS1_PRIMARY_KEY_OPTIONS,
  LINK_TYPE_OPTIONS,
  isIdentifierRecalled,
  type IdentifierDisplayStatus,
  type IdentifierRecord,
  type PrimaryKeyOption,
  type StagedDraftRecord,
};

export type IdentifierLifecycleState = 'draft' | 'pending' | 'live' | 'invalid';

/**
 * Computes the lifecycle state (`draft`, `pending`, `live`, or `invalid`) for an IdentifierRecord:
 * - `'invalid'`: `validation?.status === 'INVALID'`
 * - `'draft'`: never promoted to live yet (`version === undefined` or `0` and `defaultUrl === undefined`)
 * - `'pending'`: has a live version (`defaultUrl !== undefined` or `(version ?? 0) > 0`) AND has unpromoted staged `draft` (or `validation?.status === 'PENDING'`)
 * - `'live'`: has a valid live version with no unpromoted draft
 */
export function resolveIdentifierLifecycleState(
  record: IdentifierRecord,
): IdentifierLifecycleState {
  if (record.validation?.status === 'INVALID') {
    return 'invalid';
  }
  const hasLiveVersion =
    (record.version !== undefined && record.version > 0) || record.defaultUrl !== undefined;
  if (!hasLiveVersion) {
    return 'draft';
  }
  if (record.draft !== undefined || record.validation?.status === 'PENDING') {
    return 'pending';
  }
  return 'live';
}

/**
 * Translates technical validation error items into clear, plain-language guidance for brand/retail operators.
 */
export function formatPlainValidationError(error: {
  readonly code: string;
  readonly message: string;
  readonly field?: string;
}): string {
  const msg = error.message.replace(
    /^(?:TargetLink href '[^']+' invalid|Conditional rule '[^']+' targetHref '[^']+' invalid):\s*/i,
    '',
  );
  if (/Link safety check unavailable|could not be verified.*Web Risk|unverified/i.test(msg)) {
    return 'Link safety check unavailable — could not verify this URL with Google Web Risk. Please try saving again.';
  }
  if (/flagged as unsafe by Google Web Risk|Unsafe URL blocked by Google Web Risk/i.test(msg)) {
    return msg.replace(/\.+$/, '.');
  }
  if (/defaultLink\s*=\s*true/i.test(msg)) {
    return 'Choose exactly one default destination link so scanners know where to redirect by default.';
  }
  if (/blocked|forbidden private|loopback|metadata|non-public range/i.test(msg)) {
    return 'The destination web address is blocked for security reasons. Please use a public HTTPS address.';
  }
  if (/redirect loop|points back to resolver/i.test(msg)) {
    return 'The destination web address points back to the resolver itself, which would cause an infinite redirect loop.';
  }
  if (/https:\/\//i.test(msg)) {
    return 'All destination web addresses must start with https://.';
  }
  if (
    /GS1_SYNTAX_INVALID_CHECK_DIGIT|check digit/i.test(msg) ||
    error.code.includes('CHECK_DIGIT')
  ) {
    return 'The GS1 check digit (the last digit of the barcode number) is incorrect.';
  }
  if (/GS1_SYNTAX_RCN_NOT_PERMITTED|Restricted Circulation/i.test(msg)) {
    return 'Restricted-circulation internal store barcodes cannot be registered on the public resolver.';
  }
  if (/Company Prefix/i.test(msg)) {
    return 'This barcode number does not match any GS1 Company Prefix registered for your workspace.';
  }
  if (/gs1:epil/i.test(msg)) {
    return 'Regulated patient information leaflet (ePIL) links must point to an approved medical portal.';
  }
  if (/validFrom.*validUntil/i.test(msg)) {
    return 'The campaign start date must be earlier than the end date.';
  }
  return msg;
}

/**
 * Computes the display status badge (`valid`, `pending`, or `invalid`) for an IdentifierRecord.
 * - If `validation?.status === 'INVALID'`, returns `'invalid'`.
 * - If `draft` is present (staged edits not promoted to live yet) or `validation?.status === 'PENDING'`, returns `'pending'`.
 * - If `validation?.status === 'VALID'` (or `'WARNING'`) and no unpromoted draft is staged, returns `'valid'`.
 * - Otherwise defaults to `'pending'`.
 */
export function resolveIdentifierDisplayStatus(record: IdentifierRecord): IdentifierDisplayStatus {
  if (record.validation?.status === 'INVALID') {
    return 'invalid';
  }
  if (record.draft !== undefined || record.validation?.status === 'PENDING') {
    return 'pending';
  }
  if (record.validation?.status === 'VALID' || record.validation?.status === 'WARNING') {
    return 'valid';
  }
  return 'pending';
}

/**
 * Validates a GS1 primary key input using `@myplayer/gs1` (`parseGS1DigitalLink`).
 */
export function validateGs1KeyInput(
  selectedAi: PrimaryKeyAI,
  rawInput: string,
):
  | {
      readonly ok: true;
      readonly ai: PrimaryKeyAI;
      readonly canonicalPrimaryKey: string;
      readonly identifierKey: string;
    }
  | {
      readonly ok: false;
      readonly errorCode: string;
      readonly message: string;
      readonly clauseRef: string;
    } {
  const trimmed = rawInput.trim();
  if (trimmed.length === 0) {
    return {
      ok: false,
      errorCode: 'GS1_EMPTY_KEY',
      message: 'Enter a GS1 identification key value.',
      clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3',
    };
  }

  const candidateUri =
    trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : trimmed.startsWith('/')
        ? `https://id.gs1.org${trimmed}`
        : `https://id.gs1.org/${selectedAi}/${encodeURIComponent(trimmed)}`;

  const parsed = parseGS1DigitalLink(candidateUri);
  if (!parsed.ok) {
    return {
      ok: false,
      errorCode: parsed.errorCode,
      message: parsed.message,
      clauseRef: parsed.clauseRef,
    };
  }

  return {
    ok: true,
    ai: parsed.value.ai,
    canonicalPrimaryKey: parsed.value.primaryKey,
    identifierKey: parsed.value.identifierKey,
  };
}

/**
 * Builds the exact Firestore `create` payload permitted by `contracts/firestore/firestore.rules`:
 * Only allowed keys: `['identifierKey', 'orgId', 'workspaceId', 'ai', 'primaryKey', 'sector', 'createdBy', 'createdAt', 'updatedBy', 'updatedAt', 'draft']`.
 * Never writes server-owned or live routing fields (`status`, `defaultUrl`, `links`, `rules`, `validation`, `version`).
 */
export async function createStagedIdentifierInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly uid: string;
  readonly ai: PrimaryKeyAI;
  readonly primaryKey: string;
  readonly title: string;
  readonly brand?: string;
  readonly sector?: SectorId;
  readonly defaultUrl: string;
  readonly links?: readonly DraftTargetLink[];
  readonly knownWorkspaceIds?: readonly string[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
}): Promise<{ readonly identifierKey: string; readonly draft: IdentifierDraft }> {
  const gs1Check = validateGs1KeyInput(params.ai, params.primaryKey);
  if (!gs1Check.ok) {
    throw new Error(`${gs1Check.message} [${gs1Check.errorCode}]`);
  }

  const uniquenessConflict = await checkFirestoreOrgIdentifierUniqueness({
    db: params.db,
    identifierKey: gs1Check.identifierKey,
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    knownWorkspaceIds: params.knownWorkspaceIds,
    workspaceNameMap: params.workspaceNameMap,
  });
  if (uniquenessConflict !== null) {
    throw new Error(uniquenessConflict);
  }

  const initialLinks: readonly DraftTargetLink[] =
    params.links !== undefined && params.links.length > 0
      ? params.links
      : [
          {
            linkType: 'gs1:pip',
            href: params.defaultUrl.trim(),
            title: params.title.trim(),
            type: 'text/html',
            hreflang: ['en'],
            context: ['GLOBAL'],
            defaultLink: true,
          },
        ];

  const nowIso = new Date().toISOString();
  const draftCandidate: IdentifierDraft = {
    title: params.title.trim(),
    ...(params.brand !== undefined && params.brand.trim().length > 0
      ? { brand: params.brand.trim() }
      : {}),
    status: 'ACTIVE',
    defaultUrl: params.defaultUrl.trim(),
    links: initialLinks,
    rules: [],
    submittedBy: params.uid,
    submittedAt: nowIso,
  };

  const parsedDraft = IdentifierDraftSchema.safeParse(draftCandidate);
  if (!parsedDraft.success) {
    const firstIssue = parsedDraft.error.issues[0];
    throw new Error(firstIssue?.message ?? 'Invalid identifier draft schema');
  }

  const candidateValidation = validateIdentifierCandidate(
    gs1Check.ai,
    gs1Check.canonicalPrimaryKey,
    draftCandidate.defaultUrl,
    initialLinks.filter((l) => l.removed !== true),
    { requireSingleDefaultLink: true },
  );
  if (candidateValidation.status === 'INVALID') {
    const firstErr = candidateValidation.errors[0];
    throw new Error(firstErr?.message ?? 'Identifier candidate failed validation');
  }

  const identifierRef = doc(
    params.db,
    'organisations',
    params.orgId,
    'workspaces',
    params.workspaceId,
    'identifiers',
    gs1Check.identifierKey,
  );

  await setDoc(identifierRef, {
    identifierKey: gs1Check.identifierKey,
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    ai: gs1Check.ai,
    primaryKey: gs1Check.canonicalPrimaryKey,
    sector: params.sector ?? 'RETAIL_CPG',
    createdBy: params.uid,
    createdAt: serverTimestamp(),
    updatedBy: params.uid,
    updatedAt: serverTimestamp(),
    draft: draftCandidate,
  });

  return {
    identifierKey: gs1Check.identifierKey,
    draft: draftCandidate,
  };
}

/**
 * Stages an updated `draft` on an existing identifier document in Firestore.
 * Per `contracts/firestore/firestore.rules` (`onlyStagesDraftOnUpdate`), writes ONLY
 * `draft`, `updatedBy`, and `updatedAt: serverTimestamp()`. Never touches live fields and never deletes.
 */
export async function saveStagedDraftLinksInFirestore(params: {
  readonly db: Firestore;
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly uid: string;
  readonly ai: string;
  readonly primaryKey: string;
  readonly title: string;
  readonly brand?: string;
  readonly status?: IdentifierStatus;
  readonly defaultUrl: string;
  readonly links: readonly DraftTargetLink[];
  readonly rules?: readonly ConditionalRoutingRule[];
}): Promise<StagedDraftRecord> {
  for (const link of params.links) {
    const linkParse = TargetLinkSchema.safeParse(link);
    if (!linkParse.success) {
      const issue = linkParse.error.issues[0];
      throw new Error(
        `Invalid link "${link.title || link.href}": ${issue?.message ?? 'Invalid schema'}`,
      );
    }
  }

  const activeLinks = params.links.filter((l) => l.removed !== true);
  const effectiveDefaultUrl =
    activeLinks.find((l) => l.defaultLink)?.href ?? params.defaultUrl.trim();

  const nowIso = new Date().toISOString();
  const nextDraft: StagedDraftRecord = {
    title: params.title.trim(),
    ...(params.brand !== undefined && params.brand.trim().length > 0
      ? { brand: params.brand.trim() }
      : {}),
    status: params.status ?? 'ACTIVE',
    defaultUrl: effectiveDefaultUrl,
    links: params.links,
    rules: params.rules ?? [],
    submittedBy: params.uid,
    submittedAt: nowIso,
  };

  const draftParse = IdentifierDraftSchema.safeParse(nextDraft);
  if (!draftParse.success) {
    const issue = draftParse.error.issues[0];
    throw new Error(issue?.message ?? 'Draft failed schema validation');
  }

  const validationBlock = validateIdentifierCandidate(
    params.ai,
    params.primaryKey,
    effectiveDefaultUrl,
    activeLinks,
    { requireSingleDefaultLink: activeLinks.length > 0 },
    nextDraft.rules,
  );
  if (validationBlock.status === 'INVALID') {
    const firstErr = validationBlock.errors[0];
    throw new Error(firstErr?.message ?? 'Draft failed GS1 / URL safety validation');
  }

  const identifierRef = doc(
    params.db,
    'organisations',
    params.orgId,
    'workspaces',
    params.workspaceId,
    'identifiers',
    params.identifierKey,
  );

  await updateDoc(identifierRef, {
    draft: nextDraft,
    updatedBy: params.uid,
    updatedAt: serverTimestamp(),
  });

  return nextDraft;
}
